import { Controller } from '@hotwired/stimulus'
import { getSelectedIds, addSelection, removeSelection } from './selection_utils'

export default class extends Controller {
  connect (): void {
    const selectedIds = getSelectedIds()
    this.restoreSubCheckboxStates(selectedIds)
    this.restoreParentCheckboxStates()
    this.restoreStandaloneCheckboxStates(selectedIds)

    this.updateHeader()
  }

  disconnect (): void {
    this.resetHeader()
  }

  toggleCheckbox (event: Event): void {
    const checkbox = event.target as HTMLInputElement
    const currentId = checkbox.value
    const isParent = checkbox.dataset.parentId == null

    if (checkbox.checked) {
      addSelection(currentId)

      if (isParent) {
        this.getSubCheckboxes(currentId).forEach(sub => {
          sub.checked = true
          sub.disabled = false
          addSelection(sub.value)
        })
      } else {
        checkbox.disabled = false
        const parentId = checkbox.dataset.parentId
        if (typeof parentId === 'string' && parentId.trim() !== '') {
          this.updateParentSelectionState(parentId)
        }
      }
    } else {
      if (isParent) {
        this.getSubCheckboxes(currentId).forEach(sub => {
          sub.checked = false
          sub.disabled = false
          removeSelection(sub.value)
        })
        removeSelection(currentId)
      } else {
        checkbox.disabled = false
        removeSelection(currentId)

        const parentId = checkbox.dataset.parentId
        if (typeof parentId === 'string' && parentId.trim() !== '') {
          this.enableSiblings(parentId)
          this.updateParentSelectionState(parentId)
        }
      }
    }

    this.updateHiddenInput()
  }

  updateParentSelectionState (parentId: string): void {
    const parent = document.querySelector<HTMLInputElement>(
      `input[type="checkbox"][value="${parentId}"]:not([data-parent-id])`
    )
    if (parent == null) return

    const subcontrolIdsAttr = parent.dataset.subcontrolIds
    if (subcontrolIdsAttr == null || subcontrolIdsAttr.trim() === '') return

    const subcontrolIds = subcontrolIdsAttr.split(',').map(id => id.trim()).filter(Boolean)

    if (subcontrolIds.length === 0) {
      parent.checked = false
      removeSelection(parentId)
      return
    }

    const selectedIds = getSelectedIds()
    const allSubcontrolsSelected = subcontrolIds.every(id => selectedIds.includes(id))

    parent.checked = allSubcontrolsSelected
    if (allSubcontrolsSelected) {
      addSelection(parentId)
    } else {
      removeSelection(parentId)
    }
  }

  restoreSubCheckboxStates (selectedIds: string[]): void {
    selectedIds.forEach(id => {
      const checkbox = document.querySelector<HTMLInputElement>(
        `input[type="checkbox"][value="${id}"][data-parent-id]`
      )
      if (checkbox != null) checkbox.checked = true
    })
  }

  restoreParentCheckboxStates (): void {
    const selectedIds = getSelectedIds()

    this.getParentCheckboxes().forEach(parent => {
      const subcontrolIdsAttr = parent.dataset.subcontrolIds
      if (typeof subcontrolIdsAttr !== 'string' || subcontrolIdsAttr.trim() === '') {
        parent.checked = false
        return
      }

      const subcontrolIds = subcontrolIdsAttr.split(',').map(id => id.trim()).filter(Boolean)
      const allSelected = subcontrolIds.every(id => selectedIds.includes(id))

      parent.checked = allSelected
      if (allSelected) {
        addSelection(parent.value)
      } else {
        removeSelection(parent.value)
      }
    })
  }

  restoreStandaloneCheckboxStates (selectedIds: string[]): void {
    selectedIds.forEach(id => {
      const checkbox = document.querySelector<HTMLInputElement>(
        `input[type="checkbox"][value="${id}"]:not([data-parent-id])`
      )
      const hasSubcontrols = this.getSubCheckboxes(id).length > 0
      if ((checkbox != null) && !hasSubcontrols) {
        checkbox.checked = true
      }
    })
  }

  updateParentState (parentId: string): void {
    const parent = document.querySelector<HTMLInputElement>(
      `input[type="checkbox"][value="${parentId}"]:not([data-parent-id])`
    )
    if ((parent == null) || parent.disabled) return

    const subCheckboxes = this.getSubCheckboxes(parentId)
    const allChecked = Array.from(subCheckboxes).every(cb => cb.checked)

    parent.checked = allChecked
    allChecked ? addSelection(parentId) : removeSelection(parentId)
  }

  updateParentFromSubcontrolData (parentCheckbox: HTMLInputElement, selectedIds: string[]): void {
    const subcontrolIdsString = parentCheckbox.dataset.subcontrolIds
    if (subcontrolIdsString == null || subcontrolIdsString.trim() === '') return

    const subcontrolIds = subcontrolIdsString.split(',').map(id => id.trim()).filter(Boolean)
    const allChecked = subcontrolIds.every(id => selectedIds.includes(id))

    if (allChecked) {
      parentCheckbox.checked = true
      addSelection(parentCheckbox.value)
    } else {
      parentCheckbox.checked = false
      removeSelection(parentCheckbox.value)
    }
  }

  enableSiblings (parentId: string): void {
    this.getSubCheckboxes(parentId).forEach(cb => (cb.disabled = false))
  }

  getParentCheckboxes (): NodeListOf<HTMLInputElement> {
    return document.querySelectorAll<HTMLInputElement>(
      'input[type="checkbox"]:not([data-parent-id])'
    )
  }

  getSubCheckboxes (parentId: string): NodeListOf<HTMLInputElement> {
    return document.querySelectorAll<HTMLInputElement>(
      `input[type="checkbox"][data-parent-id="${parentId}"]`
    )
  }

  updateHiddenInput (): void {
    const hiddenInput = this.hiddenInput
    if (hiddenInput == null) return
    hiddenInput.value = getSelectedIds().join(',')
  }

  get container (): HTMLDivElement | null {
    return document.querySelector<HTMLDivElement>('#selected-control-hidden-inputs')
  }

  get hiddenInput (): HTMLInputElement | null {
    return document.querySelector<HTMLInputElement>('#selected-control-ids')
  }

  updateHeader (): void {
    const standardName = document.querySelector<HTMLInputElement>('#selected-standard-name')
    this.replaceHeading(standardName?.value)
  }

  resetHeader (): void {
    const pageHeading = document.querySelector<HTMLInputElement>('#page-heading')
    this.replaceHeading(pageHeading?.value)
  }

  replaceHeading (value?: string): void {
    const headingFrame = document.querySelector('#link-page-heading')
    const isNonEmptyString = typeof value === 'string' && value.trim() !== ''

    if (headingFrame !== null && isNonEmptyString) {
      headingFrame.innerHTML = `
        <h2 class="text-xl font-medium text-primary">
          ${value}
        </h2>
      `
    }
  }
}
